import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { UserPreferences } from '@yhelp/shared-types';
import { useAuthorizedFetch } from '@/lib/useAuthorizedFetch';
import { useMe } from '@/hooks/useMe';

type ThemeMode = NonNullable<UserPreferences['theme']>;

interface ThemeContextValue {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  tenantPrimary: string;
  tenantLogoUrl: string | null;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const DEFAULT_PRIMARY = '#FFE600';

function applyTheme(mode: ThemeMode, primary: string, logoUrl: string | null) {
  const root = document.documentElement;
  const prefersDark =
    mode === 'dark' ||
    (mode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  root.classList.toggle('dark', prefersDark);
  root.style.setProperty('--tenant-primary', primary);
  root.style.setProperty('--tenant-logo-url', logoUrl ? `url("${logoUrl}")` : 'none');
  try {
    localStorage.setItem('yhelp-theme-mode', mode);
  } catch {
    /* private browsing */
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { data: me } = useMe();
  const authorizedFetch = useAuthorizedFetch();
  const tenantPrimary = me?.data.tenant?.primary_color ?? DEFAULT_PRIMARY;
  const tenantLogoUrl = me?.data.tenant?.logo_url ?? null;
  const savedTheme = (me?.data.user as { preferences?: UserPreferences })?.preferences?.theme;
  const [mode, setModeState] = useState<ThemeMode>(savedTheme ?? 'system');

  useEffect(() => {
    if (savedTheme) setModeState(savedTheme);
  }, [savedTheme]);

  useEffect(() => {
    applyTheme(mode, tenantPrimary, tenantLogoUrl);
  }, [mode, tenantPrimary, tenantLogoUrl]);

  useEffect(() => {
    if (mode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('system', tenantPrimary, tenantLogoUrl);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [mode, tenantPrimary, tenantLogoUrl]);

  const setMode = useCallback(
    (next: ThemeMode) => {
      setModeState(next);
      void authorizedFetch('/v1/me', {
        method: 'PATCH',
        body: JSON.stringify({ preferences: { theme: next } }),
      });
    },
    [authorizedFetch],
  );

  const value = useMemo(
    () => ({ mode, setMode, tenantPrimary, tenantLogoUrl }),
    [mode, setMode, tenantPrimary, tenantLogoUrl],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
